
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useGDPR } from '@/contexts/GDPRContext';
import { X, Settings } from 'lucide-react';

export const ConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, acceptEssential, updateConsents, hideBanner } = useGDPR();
  const [showDetails, setShowDetails] = useState(false);
  const [localConsents, setLocalConsents] = useState({
    analytics: false,
    marketing: false,
    functional: true, // Toujours activé par défaut
  });

  if (!showBanner) return null;

  const handleCustomSave = () => {
    updateConsents(localConsents);
    hideBanner();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/50">
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold">{t("cookies.title")}</h3>
            <Button variant="ghost" size="sm" onClick={hideBanner}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            {t("cookies.description")}
          </p>

          {!showDetails ? (
            <div className="flex flex-wrap gap-3">
              <Button onClick={acceptAll} className="bg-green-600 hover:bg-green-700">
                {t("cookies.acceptAll")}
              </Button>
              <Button variant="outline" onClick={acceptEssential}>
                {t("cookies.essentialOnly")}
              </Button>
              <Button variant="ghost" onClick={() => setShowDetails(true)}>
                <Settings className="h-4 w-4 mr-2" />
                {t("cookies.customize")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="functional">{t("cookies.functional.title")}</Label>
                    <p className="text-xs text-gray-500">
                      {t("cookies.functional.description")}
                    </p>
                  </div>
                  <Switch id="functional" checked={true} disabled />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="analytics">{t("cookies.analytics.title")}</Label>
                    <p className="text-xs text-gray-500">
                      {t("cookies.analytics.description")}
                    </p>
                  </div>
                  <Switch 
                    id="analytics" 
                    checked={localConsents.analytics}
                    onCheckedChange={(checked) => 
                      setLocalConsents(prev => ({ ...prev, analytics: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="marketing">{t("cookies.marketing.title")}</Label>
                    <p className="text-xs text-gray-500">
                      {t("cookies.marketing.description")}
                    </p>
                  </div>
                  <Switch 
                    id="marketing" 
                    checked={localConsents.marketing}
                    onCheckedChange={(checked) => 
                      setLocalConsents(prev => ({ ...prev, marketing: checked }))
                    }
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button onClick={handleCustomSave}>
                  {t("cookies.savePreferences")}
                </Button>
                <Button variant="outline" onClick={() => setShowDetails(false)}>
                  {t("cookies.back")}
                </Button>
              </div>
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-4">
            {t("cookies.privacyNote")}{' '}
            <a href="/privacy-policy" className="text-blue-600 hover:underline">
              {t("cookies.privacyLink")}
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
